import { makeAutoObservable } from 'mobx'
import { hitLevels } from '../utils/levels'
import { pickRandom, addToObj, INIT_MOVES, COMBO_SIZE, BOARD_SIZE, getUniqueMatches } from '../utils/helpers'

export interface Cell {
  id: number
  icon: string
}

export interface LastHarvest {
  icon: string
  size: number
  isCombo: boolean
  score: number
}

export interface HarvestStore {
  // Estado del juego
  score: number
  currLevel: number
  moves: number
  shuffles: number
  bombs: number
  collected: Record<string, number>
  cells: Cell[]
  currCellId: number
  lastHarvest: LastHarvest | null
  
  // Computed
  sortCollected: [string, number][]
  bombIcon: string
  currentSet: string[]
  validMatch: boolean
  matchIds: number[]
  
  // Acciones
  shuffle: (level?: number) => void
  addMoves: (amount?: number) => void
  setCurrent: (id: number) => void
  onCollect: (icon: string, size: number) => void
  bomb: () => void
  reassignCells: (cellIds: number[], newSet: string[], excludeIcon?: string) => void
  reset: (level?: number) => void
  iconMatches: (index: number) => boolean
}

export const createHarvestStore = (): HarvestStore => {
  const initCells = (set: string[]): Cell[] => {
    return Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, id) => ({
      id,
      icon: pickRandom(set),
    }))
  }

  const store = makeAutoObservable({
    score: 0,
    currLevel: 0,
    moves: INIT_MOVES,
    shuffles: 1,
    bombs: 1,
    collected: {} as Record<string, number>,
    cells: initCells(hitLevels[0]),
    currCellId: 0,
    lastHarvest: null as LastHarvest | null,

    // Computed
    get currentSet(): string[] {
      return hitLevels[this.currLevel] || hitLevels[0]
    },

    get sortCollected(): [string, number][] {
      return Object.entries(this.collected).sort((a, b) => b[1] - a[1])
    },

    get bombIcon(): string {
      return pickRandom(this.currentSet)
    },

    get matchIds(): number[] {
      return getUniqueMatches(this.currCellId, this.cells, BOARD_SIZE, false)
    },

    get validMatch(): boolean {
      return this.matchIds.length >= 3
    },

    // Acciones
    iconMatches(index: number): boolean {
      return this.matchIds.includes(index)
    },

    shuffle(level?: number) {
      if (level !== undefined) {
        this.currLevel = level
      }
      this.collected = {}
      this.moves = INIT_MOVES + this.currLevel
      this.cells = initCells(this.currentSet)
      this.lastHarvest = null
    },

    addMoves(amount = -1) {
      this.moves += amount
    },

    setCurrent(id: number) {
      this.currCellId = Math.max(0, Math.min(id, this.cells.length - 1))
    },

    reassignCells(cellIds: number[], newSet: string[], excludeIcon?: string) {
      const filteredSet = excludeIcon 
        ? newSet.filter(icon => icon !== excludeIcon && icon !== '🕳')
        : newSet.filter(icon => icon !== '🕳')
      
      cellIds.forEach(id => {
        if (this.cells[id]) {
          this.cells[id].icon = pickRandom(filteredSet)
        }
      })
    },

    onCollect(matchIcon: string, size: number) {
      // Calcular score
      const newScore = matchIcon === '🔥' 
        ? size * 2 
        : (this.currLevel + 1) * (size > COMBO_SIZE ? size * size : size)

      this.score += newScore

      // Guardar último harvest
      this.lastHarvest = {
        icon: matchIcon,
        size,
        isCombo: size >= COMBO_SIZE,
        score: newScore,
      }

      // Añadir a colectados
      addToObj(this.collected, matchIcon, size)

      // Bonus por combo
      if (size >= COMBO_SIZE) {
        this.addMoves(size - COMBO_SIZE)
      }
    },

    bomb() {
      if (this.bombs > 0) {
        const targetIcon = this.bombIcon
        const targetCells = this.cells
          .filter(cell => cell.icon === targetIcon)
          .map(cell => cell.id)
        
        this.reassignCells(targetCells, this.currentSet, targetIcon)
        this.bombs--
      }
    },

    reset(level = 0) {
      this.score = 0
      this.currLevel = level
      this.collected = {}
      this.moves = INIT_MOVES + level
      this.shuffles = 1
      this.bombs = 1
      this.cells = initCells(hitLevels[level] || hitLevels[0])
      this.lastHarvest = null
    },
  })

  return store
}
